import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const dotColor = (health) =>
  health === "green" ? "#00DC78" :
  health === "yellow" ? "#FFB347" : "#F87171";

/**
 * Module Connection Map Modal — Ship 2 of Techie Lock-In.
 * Opens from the Cockpit's Nervous System strip. Shows every inter-module
 * data flow with live health signals so Shannon can SEE how the system talks
 * to itself, without cluttering the default Cockpit view.
 */
const ModuleFlowModal = ({ open, onClose, setTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    fetch(`${BACKEND_URL}/api/admin/module-flow`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { console.warn("[ModuleFlow] load failed:", e); setErr("Could not load the flow map. Close and reopen to retry."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const goTo = (tabKey) => {
    onClose();
    if (setTab && tabKey) {
      setTab(tabKey);
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
    }
  };

  const counts = data?.counts || { green: 0, yellow: 0, red: 0, total: 0 };

  return (
    <div data-testid="module-flow-modal" role="dialog" aria-modal="true"
         onClick={onClose}
         style={{
           position: "fixed", inset: 0, background: "rgba(3, 8, 20, 0.78)",
           backdropFilter: "blur(4px)", zIndex: 9000,
           display: "flex", alignItems: "flex-start", justifyContent: "center",
           padding: "3rem 1rem", overflowY: "auto",
         }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{
             background: "linear-gradient(180deg, #0a1929 0%, #0f1e36 100%)",
             border: "1px solid rgba(184,145,90,0.4)",
             borderRadius: 12, padding: "1.5rem 1.6rem", maxWidth: 780, width: "100%",
             boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
           }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", marginBottom: "1rem" }}>
          <div>
            <div style={{ color: "#B8915A", fontSize: "0.72rem", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 800 }}>
              🕸️ How Your System Talks
            </div>
            <h2 data-testid="module-flow-title" style={{ color: "#F5EFE1", fontSize: "1.35rem", margin: "0.3rem 0 0", fontWeight: 700 }}>
              Module Connection Map
            </h2>
            <div style={{ color: "#ADBED1", fontSize: "0.82rem", marginTop: 4 }}>
              {counts.total > 0 ? (
                <>
                  <strong style={{ color: "#00DC78" }}>{counts.green}</strong> green ·{" "}
                  <strong style={{ color: "#FFB347" }}>{counts.yellow}</strong> yellow ·{" "}
                  <strong style={{ color: "#F87171" }}>{counts.red}</strong> red · {counts.total} total flows
                </>
              ) : "Loading connections…"}
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="module-flow-close"
                  style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.25)",
                           color: "#F5EFE1", padding: "0.45rem 0.9rem", borderRadius: 4,
                           fontSize: "0.85rem", fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
            ✕ Close
          </button>
        </div>

        {loading && (
          <div style={{ color: "#ADBED1", padding: "2rem 0", textAlign: "center" }}>Loading connections…</div>
        )}

        {err && (
          <div data-testid="module-flow-error" style={{
            background: "rgba(220,38,38,0.14)", color: "#FCA5A5",
            border: "1px solid rgba(220,38,38,0.4)", borderRadius: 6, padding: "0.7rem 1rem",
          }}>{err}</div>
        )}

        {data && !loading && !err && (
          <div style={{ display: "grid", gap: "1.1rem" }}>
            {(data.domains || []).map((domain) => (
              <div key={domain.key} data-testid={`flow-domain-${domain.key}`}>
                <div style={{ color: "#B8915A", fontSize: "0.72rem", letterSpacing: "0.16em",
                              textTransform: "uppercase", fontWeight: 800, marginBottom: "0.5rem" }}>
                  {domain.label}
                </div>
                <div style={{ display: "grid", gap: "0.4rem" }}>
                  {(domain.edges || []).map((edge, i) => (
                    <div key={i} data-testid={`flow-edge-${domain.key}-${i}`}
                         style={{
                           background: "rgba(255,255,255,0.03)",
                           border: "1px solid rgba(255,255,255,0.06)",
                           borderLeft: `3px solid ${dotColor(edge.health)}`,
                           borderRadius: 5, padding: "0.65rem 0.85rem",
                         }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.55rem",
                                    fontSize: "0.86rem", color: "#F3F4F6", fontWeight: 600, flexWrap: "wrap" }}>
                        <span>{edge.from}</span>
                        <span style={{ color: "#6B7280" }}>→</span>
                        <span>{edge.to}</span>
                        <span style={{
                          width: 8, height: 8, borderRadius: "50%", background: dotColor(edge.health),
                          boxShadow: `0 0 6px ${dotColor(edge.health)}`, marginLeft: 4,
                        }} />
                      </div>
                      {edge.label && (
                        <div style={{ color: "#ADBED1", fontSize: "0.76rem", marginTop: 3 }}>
                          {edge.label}
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                                    marginTop: 4, gap: "0.5rem", flexWrap: "wrap" }}>
                        <div style={{ color: "#9CA3AF", fontSize: "0.72rem" }}>
                          {edge.detail}
                          {edge.count_24h != null && (
                            <> · <strong>{edge.count_24h}</strong> in 24h</>
                          )}
                          {edge.last_activity && (
                            <> · last {new Date(edge.last_activity).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                          )}
                        </div>
                        {edge.deep_link && (
                          <button type="button" onClick={() => goTo(edge.deep_link)}
                                  data-testid={`flow-open-${domain.key}-${i}`}
                                  style={{ background: "transparent", color: "#B8915A",
                                           border: "1px solid rgba(184,145,90,0.5)",
                                           padding: "0.2rem 0.55rem", borderRadius: 3,
                                           fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}>
                            Open →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ color: "#6B7280", fontSize: "0.72rem", textAlign: "center", marginTop: "0.4rem" }}>
              Snapshot at {new Date(data.generated_at).toLocaleTimeString()} · every dot pulls from live data
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleFlowModal;
